class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    const outsidePanel = event => !event.composedPath().some(node =>
      node instanceof Element && node.matches('.drawer__inner'));
    window.addEventListener('wheel', event => {
      if (this.classList.contains('active') && outsidePanel(event) && Math.abs(event.deltaY) > 2) this.close();
    }, { passive: true });
    let touchStartY = null;
    window.addEventListener('touchstart', event => {
      touchStartY = outsidePanel(event) ? event.touches[0]?.clientY ?? null : null;
    }, { passive: true });
    window.addEventListener('touchmove', event => {
      if (this.classList.contains('active') && touchStartY !== null &&
          Math.abs((event.touches[0]?.clientY ?? touchStartY) - touchStartY) > 10) {
        this.close(); touchStartY = null;
      }
    }, { passive: true });
    window.addEventListener('scroll', () => {
      if (this.classList.contains('active') && Math.abs(window.scrollY - (this.hilaliOpenScrollY ?? window.scrollY)) > 8) this.close();
    }, { passive: true });
    this.hilaliLatestObserver = new MutationObserver(() => this.moveLatestItemFirst());
    this.hilaliLatestObserver.observe(this, { childList: true, subtree: true });
    this.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'mouse' && event.target.closest('.drawer__inner')) {
        this.hilaliHover = true;
        clearTimeout(this.hilaliTimer);
      }
    });
    this.addEventListener('pointerout', (event) => {
      if (event.pointerType === 'mouse' && event.target.closest('.drawer__inner') &&
          !event.relatedTarget?.closest?.('.drawer__inner')) {
        this.hilaliHover = false;
        this.scheduleHilaliClose();
      }
    });
    this.addEventListener('pointerdown', () => clearTimeout(this.hilaliTimer));
    this.addEventListener('pointerup', () => this.scheduleHilaliClose());
    this.addEventListener('pointercancel', () => this.scheduleHilaliClose());
    this.addEventListener('input', () => this.scheduleHilaliClose());
    this.addEventListener('focusin', () => {
      if (this.querySelector('input:focus, textarea:focus, select:focus')) clearTimeout(this.hilaliTimer);
    });
    this.addEventListener('focusout', () => this.scheduleHilaliClose());
    this.addEventListener('scroll', () => this.scheduleHilaliClose(), true);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, hilaliAutoClose = false) {
    // Manual opens stay open. Only post-add renderContents opts into the idle timer.
    if (this.classList.contains('active')) {
      if (!hilaliAutoClose) {
        this.hilaliAutoClose = false;
        clearTimeout(this.hilaliTimer);
      }
      return;
    }
    this.hilaliAutoClose = hilaliAutoClose;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.scheduleHilaliClose();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    this.hilaliOpenScrollY = window.scrollY;
    // Popover permits page scrolling; scrolling outside closes it.
    document.body.classList.remove('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  scheduleHilaliClose() {
    clearTimeout(this.hilaliTimer);
    if (!this.hilaliAutoClose || !this.classList.contains('active') || this.hilaliHover) return;
    this.hilaliTimer = setTimeout(() => {
      if (this.hilaliHover || this.querySelector('input:focus, textarea:focus, select:focus')) return;
      this.close();
    }, 3000);
  }

  close() {
    this.hilaliAutoClose = false;
    clearTimeout(this.hilaliTimer);
    this.hilaliHover = false;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  moveLatestItemFirst() {
    if (!this.hilaliLatestKey && !this.hilaliLatestVariant) return;
    const rows = [...this.querySelectorAll('.cart-items tbody > tr.cart-item')];
    const row = rows.find(row => {
      const input = row.querySelector('input[data-quantity-line-key]');
      return input && this.hilaliLatestKey && input.dataset.quantityLineKey === this.hilaliLatestKey;
    }) || rows.find(row => {
      const input = row.querySelector('input[data-quantity-variant-id]');
      return input && this.hilaliLatestVariant && input.dataset.quantityVariantId === this.hilaliLatestVariant;
    });
    if (!row || row.classList.contains('cart-item__nested-line')) return;
    if (row.parentElement.firstElementChild !== row) row.parentElement.prepend(row);
  }

  renderContents(parsedState) {
    const latest = parsedState.items?.[parsedState.items.length - 1] || parsedState;
    this.hilaliLatestKey = latest.key || null;
    this.hilaliLatestVariant = latest.variant_id || latest.id ? String(latest.variant_id || latest.id) : null;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.moveLatestItemFirst();
    const itemList = this.querySelector('cart-drawer-items');
    if (itemList) itemList.scrollTop = 0;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open(null, true);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

